import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AddChargeRequest, ChargeType } from '../../api/types';
import { uploadChargeAttachment } from '../../api/endpoints/payments';
import { moneyInputError, paiseToDecimal, parsePaise } from './allocation';
import { ProofInput } from './ProofInput';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
import { DialogHelp } from '../../help/DialogHelp';

function newRequestId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `charge-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function wholeRupeeError(value: string) {
  const error = moneyInputError(value);
  if (error) return error;
  return parsePaise(value) % 100n === 0n ? null : 'Enter a whole-rupee amount.';
}

export function AddChargeDialog({
  shipmentId,
  shipperLockedReason,
  partnerLockedReason,
  onClose,
  onUploadProof = (file) => uploadChargeAttachment(file, shipmentId),
  onSubmit,
  submitting = false,
}: {
  shipmentId: number;
  onClose: () => void;
  onUploadProof?: (file: File) => Promise<string>;
  onSubmit: (request: AddChargeRequest) => void | Promise<void>;
  submitting?: boolean;
  shipperLockedReason?: string;
  partnerLockedReason?: string;
}) {
  const { t } = useTranslation();
  const requestId = useRef(newRequestId());
  const [type, setType] = useState<ChargeType>('HALTING');
  const [description, setDescription] = useState('');
  const [shipperAmount, setShipperAmount] = useState('');
  const [partnerAmount, setPartnerAmount] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<{ file: File; id: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const effectiveShipperAmount = shipperLockedReason ? '0' : shipperAmount;
  const effectivePartnerAmount = partnerLockedReason ? '0' : partnerAmount;
  const shipperError = effectiveShipperAmount ? wholeRupeeError(effectiveShipperAmount) : null;
  const partnerError = effectivePartnerAmount ? wholeRupeeError(effectivePartnerAmount) : null;
  const locks = useRef({ shipper: shipperLockedReason, partner: partnerLockedReason });
  locks.current = { shipper: shipperLockedReason, partner: partnerLockedReason };
  const inFlight = useRef(false);
  const descriptionValid =
    description.trim().length <= 200 && (type !== 'OTHER' || description.trim().length > 0);
  const valid =
    descriptionValid &&
    !shipperError &&
    !partnerError &&
    effectiveShipperAmount !== '' &&
    effectivePartnerAmount !== '' &&
    parsePaise(effectiveShipperAmount) + parsePaise(effectivePartnerAmount) > 0n;
  const validationMessage =
    shipperLockedReason && partnerLockedReason
      ? 'Both amounts are locked. A charge cannot be added.'
      : !descriptionValid
        ? description.trim().length > 200
          ? 'Description must be at most 200 characters.'
          : 'Add a description for Other.'
        : !valid
          ? 'Enter a nonnegative whole-rupee amount for each editable side (use 0 if unchanged). At least one editable amount must be greater than 0.'
          : null;

  const submit = async () => {
    if (!valid || inFlight.current || submitting) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      let proofUploadId = proof && uploaded?.file === proof ? uploaded.id : undefined;
      if (proof && !proofUploadId) {
        proofUploadId = await onUploadProof(proof);
        setUploaded({ file: proof, id: proofUploadId });
      }
      // A summary refresh can lock a side while proof upload is in flight.
      const shipperValue = locks.current.shipper ? '0' : shipperAmount;
      const partnerValue = locks.current.partner ? '0' : partnerAmount;
      if (
        !shipperValue ||
        !partnerValue ||
        wholeRupeeError(shipperValue) ||
        wholeRupeeError(partnerValue) ||
        parsePaise(shipperValue) + parsePaise(partnerValue) === 0n
      ) {
        throw new Error('Charge eligibility changed. Check the editable amounts and try again.');
      }
      await onSubmit({
        clientRequestId: requestId.current,
        type,
        description: description.trim(),
        shipperAmount: paiseToDecimal(parsePaise(shipperValue)),
        partnerAmount: paiseToDecimal(parsePaise(partnerValue)),
        proofUploadId,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We couldn’t add this charge.');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className={styles.scrim}>
      <section role="dialog" aria-modal="true" aria-label={t('payments.addChargeDialog.title')} className={styles.dialog}>
        <header className={styles.header}>
          <div>
            <h2>{t('payments.addChargeDialog.title')}</h2>
            <p>{t('payments.addChargeDialog.subtitle')}</p>
          </div>
          <div className={styles.actions}>
            <DialogHelp context="add-charge" title={t('payments.addChargeDialog.title')} />
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!valid || busy || submitting}
              onClick={submit}
            >
              {busy || submitting ? 'Saving…' : 'Add charge'}
            </Button>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.card}>
            <p>
              {t('payments.addChargeDialog.intro')}
            </p>
            <div className={styles.grid}>
              <label className={styles.field}>
                {t('payments.addChargeDialog.chargeType')}
                <select
                  value={type}
                  onChange={(event) => setType(event.target.value as ChargeType)}
                >
                  {['HALTING', 'LOADING', 'UNLOADING', 'OTHER'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                {t('payments.addChargeDialog.description')}{type === 'OTHER' ? ' (required)' : ''}
                <input
                  aria-label={t('payments.addChargeDialog.description')}
                  maxLength={200}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
                {type === 'OTHER' && description.length > 0 && !description.trim() && (
                  <span role="alert" className={styles.error}>
                    {t('payments.addChargeDialog.descriptionRequired')}
                  </span>
                )}
              </label>
              <label className={styles.field}>
                {t('payments.addChargeDialog.shipperAmount')}
                <input
                  aria-label={t('payments.addChargeDialog.shipperAmount')}
                  inputMode="decimal"
                  disabled={Boolean(shipperLockedReason)}
                  value={effectiveShipperAmount}
                  onChange={(event) => {
                    const previous = shipperAmount;
                    setShipperAmount(event.target.value);
                    if (!partnerLockedReason && (!partnerAmount || partnerAmount === previous))
                      setPartnerAmount(event.target.value);
                  }}
                />
                {shipperLockedReason && (
                  <span className={styles.subtle}>{shipperLockedReason}</span>
                )}
                {shipperError && (
                  <span role="alert" className={styles.error}>
                    {shipperError}
                  </span>
                )}
              </label>
              <label className={styles.field}>
                {t('payments.addChargeDialog.partnerAmount')}
                <input
                  aria-label={t('payments.addChargeDialog.partnerAmount')}
                  inputMode="decimal"
                  disabled={Boolean(partnerLockedReason)}
                  value={effectivePartnerAmount}
                  onChange={(event) => setPartnerAmount(event.target.value)}
                />
                {partnerLockedReason && (
                  <span className={styles.subtle}>{partnerLockedReason}</span>
                )}
                {partnerError && (
                  <span role="alert" className={styles.error}>
                    {partnerError}
                  </span>
                )}
              </label>
              <div className={styles.full}>
                <ProofInput
                  required={false}
                  value={proof}
                  onChange={(file) => {
                    setProof(file);
                    setUploaded((old) => (old?.file === file ? old : null));
                  }}
                />
              </div>
              {validationMessage && (
                <div role="status" className={styles.subtle}>
                  {validationMessage}
                </div>
              )}
              {error && (
                <div role="alert" className={styles.error}>
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
